import { useCallback, useEffect, useRef, useState } from "react";
import { HowToPlay } from "../components/HowToPlay.tsx";
import { CalendarIcon, FriendsIcon, HistoryIcon, InfinityIcon, ModeIcon, StatsIcon, TrophyIcon } from "../components/HomeIcons.tsx";
import { InstallCard } from "../components/InstallCard.tsx";
import { SupportSheet } from "../components/SupportSheet.tsx";
import { SoundSettingsSheet } from "../components/SoundSettingsSheet.tsx";
import { loadAppConfig } from "../lib/appConfig.ts";
import { api } from "../lib/api.ts";
import type { FriendsData } from "../features/friends/types.ts";
import { currentAppDistribution, resolveSupportHref } from "../lib/distribution.ts";
import { APP_EDITION, upperLabel } from "../lib/edition.ts";
import type { RegisterBackHandler, Screen } from "../lib/navigation.ts";
import { useOnline } from "../lib/pwa.ts";
import { useSession } from "../lib/session.ts";
import { getSocket } from "../lib/socket.ts";
import { playSound } from "../lib/sound.ts";
import { useSoundPreference } from "../lib/useSoundPreference.ts";
import { messages } from "../i18n/index.ts";

interface HomeScreenProps {
  onNavigate: (screen: Screen) => void;
  /** Düello ekranını açıp aramayı hemen başlatır. */
  onFindOpponent: () => void;
  onSignIn: (reason: string) => void;
  registerBackHandler: RegisterBackHandler;
}

export function HomeScreen({ onNavigate, onFindOpponent, onSignIn, registerBackHandler }: HomeScreenProps) {
  const { status, user, stats } = useSession();
  const authed = status === "authed";
  const [helpOpen, setHelpOpen] = useState(false);
  const [supportOpen, setSupportOpen] = useState(false);
  const [soundOpen, setSoundOpen] = useState(false);
  const [supportHref, setSupportHref] = useState<string | null>(null);
  const [pending, setPending] = useState(0);
  // Sayı yerine kimlik kümesi: yinelenen ya da kaçan presence olayı sayacı
  // kaydırmasın.
  const [onlineFriendIds, setOnlineFriendIds] = useState<ReadonlySet<string>>(new Set());
  const pendingLoadVersion = useRef(0);
  const online = useOnline();
  const sound = useSoundPreference();

  const openSound = useCallback(() => {
    window.history.pushState({ mkScreen: "home", mkOverlay: "sound" }, "");
    setSoundOpen(true);
    playSound("ui-click", { userGesture: true });
  }, []);

  const closeSound = useCallback(() => {
    if (soundOpen) window.history.back();
  }, [soundOpen]);

  const openHelp = useCallback(() => {
    window.history.pushState({ mkScreen: "home", mkOverlay: "howto" }, "");
    setHelpOpen(true);
  }, []);

  const closeHelp = useCallback(() => {
    if (helpOpen) window.history.back();
  }, [helpOpen]);

  const openSupport = useCallback(() => {
    window.history.pushState({ mkScreen: "home", mkOverlay: "support" }, "");
    setSupportOpen(true);
  }, []);

  const closeSupport = useCallback(() => {
    if (supportOpen) window.history.back();
  }, [supportOpen]);

  useEffect(() => {
    if (!helpOpen && !supportOpen && !soundOpen) return;
    return registerBackHandler(() => {
      setHelpOpen(false);
      setSupportOpen(false);
      setSoundOpen(false);
      return "consume";
    });
  }, [helpOpen, registerBackHandler, soundOpen, supportOpen]);

  useEffect(() => {
    let active = true;
    void loadAppConfig().then((config) => {
      if (!active) return;
      setSupportHref(
        resolveSupportHref(
          config.supportUrl,
          config.supportInPlay,
          currentAppDistribution(),
          APP_EDITION,
        ),
      );
    });
    return () => {
      active = false;
    };
  }, []);

  const refreshPending = useCallback(() => {
    const version = ++pendingLoadVersion.current;
    if (!authed || !online) {
      setPending(0);
      setOnlineFriendIds(new Set());
      return;
    }
    void api<FriendsData>("/api/friends")
      .then((data) => {
        if (version === pendingLoadVersion.current) {
          setPending(data.incoming.length + data.challenges.length + data.teamInvites.length);
          setOnlineFriendIds(new Set(
            data.friends.filter((friend) => friend.online).map((friend) => friend.user.id),
          ));
        }
      })
      .catch(() => {
        /* rozet gösterilemezse sorun değil */
      });
  }, [authed, online]);

  // Bekleyen arkadaşlık isteği + meydan okuma sayısı — rozet için.
  useEffect(() => {
    refreshPending();
  }, [refreshPending]);

  useEffect(() => {
    if (!authed || !online) return;
    const socket = getSocket();
    /*
     * Presence tek tek geliyor; listeyi baştan çekmek yerine sayacı yerinde
     * oynatıyoruz. Aksi hâlde bir arkadaş her girip çıktığında /api/friends
     * yeniden isteniyor olurdu.
     */
    const onPresence = ({ userId, online: isOnline }: { userId: string; online: boolean }) => {
      setOnlineFriendIds((current) => {
        if (current.has(userId) === isOnline) return current;
        const next = new Set(current);
        if (isOnline) next.add(userId);
        else next.delete(userId);
        return next;
      });
    };
    socket.on("connect", refreshPending);
    socket.on("friends:changed", refreshPending);
    socket.on("challenge:incoming", refreshPending);
    socket.on("challenge:cancelled", refreshPending);
    socket.on("team:invite:incoming", refreshPending);
    socket.on("team:invite:cancelled", refreshPending);
    socket.on("friend:presence", onPresence);
    return () => {
      socket.off("connect", refreshPending);
      socket.off("friends:changed", refreshPending);
      socket.off("challenge:incoming", refreshPending);
      socket.off("challenge:cancelled", refreshPending);
      socket.off("team:invite:incoming", refreshPending);
      socket.off("team:invite:cancelled", refreshPending);
      socket.off("friend:presence", onPresence);
    };
  }, [authed, online, refreshPending]);

  function guarded(screen: Screen, reason: string) {
    return () => (authed ? onNavigate(screen) : onSignIn(reason));
  }

  return (
    <div className="app">
      <header className="home-header flex items-center justify-between gap-3 px-5 pt-4 pb-3">
        <h1 className="flex flex-col gap-0.5">
          <span className="text-[0.56rem] font-extrabold tracking-[0.3em] text-muted">
            {messages.home.brandTop}
          </span>
          <span className="scoreboard flex items-baseline gap-1 text-[1.375rem]">
            {messages.home.brandBottom}
            {messages.home.brandAccent && <span className="text-mint">{messages.home.brandAccent}</span>}
          </span>
        </h1>

        <div className="home-header__actions flex min-w-0 items-center gap-2">
          {supportHref && (
            <button
              type="button"
              className="btn-ghost btn-support"
              onClick={openSupport}
              aria-label={messages.support.open}
              title={messages.support.open}
            >
              <span aria-hidden="true">♥</span>
            </button>
          )}
          <button
            type="button"
            className="btn-ghost btn-sound"
            onClick={openSound}
            data-sound-control
            aria-haspopup="dialog"
            aria-expanded={soundOpen}
            aria-label={messages.sound.open}
            title={messages.sound.percent(sound.volume)}
          >
            {sound.enabled ? (
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M4 9v6h4l5 4V5L8 9H4Z" />
                <path d="M16 9a4 4 0 0 1 0 6M18.5 6.5a7.5 7.5 0 0 1 0 11" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M4 9v6h4l5 4V5L8 9H4Z" />
                <path d="m16.5 9.5 5 5m0-5-5 5" />
              </svg>
            )}
          </button>
          <button
            type="button"
            className="btn-ghost"
            onClick={openHelp}
            aria-label={messages.home.howTo}
          >
            ?
          </button>
          {authed ? (
            <button
              type="button"
              className="avatar-chip"
              onClick={() => onNavigate("profile")}
              aria-label={user ? messages.home.openProfile(user.username) : undefined}
              title={user?.username}
            >
              <span aria-hidden="true">{user ? upperLabel([...user.username][0] ?? "") : ""}</span>
              {user?.removeAds && (
                <i className="avatar-chip__star" role="img" aria-label={messages.profile.supporter}>★</i>
              )}
            </button>
          ) : (
            <button
              type="button"
              className="user-chip user-chip--sign-in"
              onClick={() =>
                onSignIn(messages.home.accountReason)
              }
            >
              {messages.home.signIn}
            </button>
          )}
        </div>
      </header>

      <main className="overflow-y-auto px-5 pt-2 pb-6">
        <div className="mx-auto flex w-full max-w-md flex-col gap-3.5">
          <InstallCard />

          {!online && (
            <p className="offline-note" role="status">
              {messages.home.offline}
            </p>
          )}

          <button
            type="button"
            className="hero-duel"
            disabled={!online}
            onClick={() => (authed ? onFindOpponent() : onSignIn(messages.home.duelReason))}
          >
            <span className="stripes stripes--moving" aria-hidden="true" style={{ left: "62%" }} />
            <span className="hero-duel__inner">
              <span className="flex items-center gap-2">
                <span className="live-dot" aria-hidden="true" />
                <span className="eyebrow eyebrow--live">
                  {online ? messages.home.liveMatch : messages.home.connectionRequired}
                </span>
              </span>

              <span className="hero-duel__title">{messages.home.duel}</span>

              <span className="hero-duel__desc">
                {messages.home.duelDescription}
              </span>

              <span className="mt-1 flex flex-wrap items-center gap-3.5">
                <span className="btn-flame">{messages.home.findOpponent}</span>
                {authed && (
                  <span className="tally">
                    <span>
                      <b className="text-mint">{stats?.duelWins ?? 0}</b>
                      <span>{messages.home.wins}</span>
                    </span>
                    <span>
                      <b className="text-flame">{stats?.duelLosses ?? 0}</b>
                      <span>{messages.home.losses}</span>
                    </span>
                  </span>
                )}
              </span>
            </span>
          </button>

          <button
            type="button"
            className="team-home-card"
            disabled={!online}
            onClick={guarded("team", messages.home.teamReason)}
          >
            <span className="stripes stripes--moving team-home-card__stripes" aria-hidden="true" />
            <span className="team-home-card__lanes" aria-hidden="true"><i /><i /></span>
            <span className="team-home-card__copy">
              <span className="eyebrow">2 × 2 · X / Y</span>
              <span className="mode-card__title">{messages.home.team}</span>
              <span className="mode-card__desc">{messages.home.teamDescription}</span>
            </span>
          </button>

          <button
            type="button"
            className="mode-row"
            disabled={!online}
            onClick={guarded("daily", messages.home.dailyReason)}
          >
            <span className="mode-row__main">
              <ModeIcon className="text-sky"><CalendarIcon /></ModeIcon>
              <span className="mode-row__text">
                <span className="mode-card__title">{messages.home.daily}</span>
                <span className="mode-card__desc">
                  {authed ? messages.home.dailyAuthed : messages.home.dailyAnon}
                </span>
              </span>
            </span>
            {authed && (
              <span className="streak-pill">
                <b>{stats?.currentStreak ?? 0}</b>
                <small>{messages.home.streakLabel}</small>
              </span>
            )}
          </button>

          <button type="button" className="mode-row" onClick={() => onNavigate("practice")}>
            <span className="mode-row__main">
              <ModeIcon className="text-mint"><InfinityIcon /></ModeIcon>
              <span className="mode-row__text">
                <span className="mode-card__title">{messages.home.solo}</span>
                <span className="mode-card__desc">{messages.home.soloDescription}</span>
              </span>
            </span>
          </button>

          <button
            type="button"
            className="mode-row"
            disabled={!online}
            onClick={guarded("friends", messages.home.friendsReason)}
          >
            <span className="mode-row__main">
              <ModeIcon className="text-flame"><FriendsIcon /></ModeIcon>
              <span className="mode-row__text">
                <span className="mode-card__title">{messages.home.friends}</span>
                <span className="mode-card__desc">{messages.home.friendsDescription}</span>
              </span>
            </span>
            <span className="mode-row__badges">
              {onlineFriendIds.size > 0 && (
                <span className="pill-online">
                  <i className="presence-dot" data-online="true" aria-hidden="true" />
                  {messages.home.friendsOnline(onlineFriendIds.size)}
                </span>
              )}
              {pending > 0 && <span className="pill-flame">{messages.home.requests(pending)}</span>}
            </span>
          </button>

          <div className="grid grid-cols-2 gap-2">
            <button type="button" className="mode-tile" onClick={() => onNavigate("history")}>
              <ModeIcon className="text-muted"><HistoryIcon /></ModeIcon>
              <span className="mode-card__title">{messages.home.matchHistory}</span>
            </button>
            <button type="button" className="mode-tile" onClick={() => onNavigate("statistics")}>
              <ModeIcon className="text-mint"><StatsIcon /></ModeIcon>
              <span className="mode-card__title">{messages.profile.statistics}</span>
            </button>
          </div>
          <button type="button" className="mode-row" disabled={!online}
            onClick={guarded("leaderboard", messages.home.accountReason)}>
            <span className="mode-row__main">
              <ModeIcon className="text-amber"><TrophyIcon /></ModeIcon>
              <span className="mode-card__title">{upperLabel(messages.leaderboard.title)}</span>
            </span>
          </button>

        </div>
      </main>

      <HowToPlay open={helpOpen} onClose={closeHelp} />
      {supportHref && (
        <SupportSheet open={supportOpen} href={supportHref} onClose={closeSupport} />
      )}
      <SoundSettingsSheet
        open={soundOpen}
        volume={sound.volume}
        onVolumeChange={sound.setVolume}
        onToggleMuted={() => sound.setEnabled(!sound.enabled)}
        onClose={closeSound}
      />
    </div>
  );
}
